import React, { useEffect, useState, useCallback } from 'react';
import { Country } from 'country-state-city';
import { useAuth } from '../../../context/AuthContext';
import { useParams } from 'react-router-dom';
import {
  submitComplianceAnswers,
  getComplianceResults,
  getComplianceQuestions,
  getComplianceStatus,
  uploadComplianceDocument,
  getComplianceLatestResult,
  listComplianceAssessments,
  getComplianceAssessmentDetail,
  COMPLIANCE_DOMAIN_LABELS,
  COMPLIANCE_STATUS_LABELS,
  type ComplianceLatestResultQuestion,
  type ComplianceDimensionDetail,
  type ComplianceAssessmentSummary,
} from '../../../api/complianceApi';
import { fetchCompanyProfile, type CompanyProfile } from '../../../api/companyProfileApi';
import { fetchEnterpriseProfile, type EnterpriseProfile } from '../../../api/enterpriseApi';
import {
  getAllRegulations,
  getJurisdictions as getDashboardJurisdictions,
  type Jurisdiction as DashboardJurisdiction,
  type RegulationSummary,
} from '../../../api/dashboardApi';
import { ComplianceFinalResult } from '../../../hooks/useComplianceSocket';
import { useCompliance } from '../../../context/ComplianceContext';
import { cached } from '../../../api/apiCache';
import ComplianceVisualizer from '../components/ComplianceVisualizer';
import ComplianceReportPanel from '../components/ComplianceReportPanel';
import styles from './GovernanceStartPage.module.css';

// ── Constants ──────────────────────────────────────────────────────────────

const AREA_LABELS: Record<string, string> = COMPLIANCE_DOMAIN_LABELS;
const STATUS_LABELS: Record<string, string> = COMPLIANCE_STATUS_LABELS;

const ALL_COUNTRIES = Country.getAllCountries();
const COUNTRY_NAME_ALIASES: Record<string, string> = {
  usa: 'United States of America', us: 'United States of America',
  uk: 'United Kingdom', uae: 'United Arab Emirates',
};

const SEVERITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
const SCOPE_LABELS: Record<JurisdictionScope, string> = { primary: 'Primary', active: 'Active', upcoming: 'Upcoming', operating: 'Operating' };

type JurisdictionScope = 'primary' | 'active' | 'upcoming' | 'operating';
type CompanyJurisdictionOption = { value: string; label: string; scope: JurisdictionScope };

// ── Jurisdiction helpers (same as governance/risk) ─────────────────────────

function normalizeJurisdiction(v: string | null | undefined): string { return (v ?? '').toLowerCase().replace(/[–—,-]/g, ' ').replace(/\s+/g, ' ').trim(); }
function formatJurisdictionDisplay(v: string | null | undefined): string { const r = v?.trim(); if (!r) return ''; const p = r.split(/\s*[–—-]\s*|\s*,\s*/).map(s => s.trim()).filter(Boolean); if (!p.length) return r; return p.filter((s, i) => p.findIndex(c => normalizeJurisdiction(c) === normalizeJurisdiction(s)) === i).join(', '); }

function pushJurisdictionOption(opts: CompanyJurisdictionOption[], seen: Set<string>, v: string | null | undefined, scope: JurisdictionScope) {
  const cv = formatJurisdictionDisplay(v); if (!cv) return; const k = normalizeJurisdiction(cv); if (!k || seen.has(k)) return;
  const prefix = scope === 'primary' ? 'Primary' : scope === 'active' ? 'Active' : scope === 'upcoming' ? 'Upcoming' : 'Operating';
  seen.add(k); opts.push({ value: cv, label: `${prefix} · ${cv}`, scope });
}

function buildJurisdictionOptions(ep: EnterpriseProfile | null, cp: CompanyProfile | null): CompanyJurisdictionOption[] {
  const opts: CompanyJurisdictionOption[] = []; const seen = new Set<string>();
  pushJurisdictionOption(opts, seen, ep?.jurisdiction, 'primary');
  ep?.other_jurisdictions?.forEach(v => pushJurisdictionOption(opts, seen, v, 'active'));
  ep?.new_jurisdictions?.forEach(v => pushJurisdictionOption(opts, seen, v, 'upcoming'));
  cp?.operating_jurisdictions?.forEach(e => {
    const same = normalizeJurisdiction(e.country) === normalizeJurisdiction(e.state ?? '');
    pushJurisdictionOption(opts, seen, same ? e.country : [e.state, e.country].filter(Boolean).join(', '), 'operating');
  });
  return opts;
}

function matchesDashboardJurisdiction(j: DashboardJurisdiction, sel: string): boolean {
  const t = normalizeJurisdiction(sel), n = normalizeJurisdiction(j.name), c = normalizeJurisdiction(j.country_name);
  const parts = formatJurisdictionDisplay(sel).split(/\s*,\s*/).map(s => s.trim()).filter(Boolean);
  const ncp = parts.filter(p => !isKnownCountry(p)).map(p => normalizeJurisdiction(p));
  const comb = normalizeJurisdiction([j.name, j.country_name].filter(Boolean).join(' '));
  const rcomb = normalizeJurisdiction([j.country_name, j.name].filter(Boolean).join(' '));
  if (ncp.length > 0) return ncp.some(p => n === p || comb === p || rcomb === p || n.startsWith(`${p} `) || p.startsWith(`${n} `));
  if (n === t || c === t || comb === t || rcomb === t) return true;
  if (n.startsWith(`${t} `) || c.startsWith(`${t} `) || t.startsWith(`${n} `) || t.startsWith(`${c} `)) return true;
  return false;
}

function getJurisdictionCodeCandidates(js: DashboardJurisdiction[]): string[] { return Array.from(new Set(js.map(j => j.code?.trim()).filter((c): c is string => !!c))); }

async function searchJurisdictionCandidates(sel: string): Promise<DashboardJurisdiction[]> {
  const f = formatJurisdictionDisplay(sel); const parts = f.split(/\s*,\s*/).map(s => s.trim()).filter(Boolean);
  const terms = Array.from(new Set([f, ...parts, ...parts.filter(p => !isKnownCountry(p)), parts[0] || '', parts.length > 1 ? parts[parts.length - 1] : ''].filter(Boolean)));
  const results = await Promise.all(terms.map(t => cached(`juris:${t}`, 300_000, () => getDashboardJurisdictions({ q: t, limit: 100 }))));
  const m = new Map<string, DashboardJurisdiction>(); results.flat().forEach(j => m.set(j.id, j)); return Array.from(m.values());
}

function isKnownCountry(v: string | null | undefined): boolean { const n = normalizeJurisdiction(v); if (!n) return false; if (COUNTRY_NAME_ALIASES[n]) return true; return ALL_COUNTRIES.some(c => normalizeJurisdiction(c.name) === n || normalizeJurisdiction(c.isoCode) === n); }
function resolveCountryName(v: string | null | undefined): string { const f = formatJurisdictionDisplay(v); const n = normalizeJurisdiction(f); if (!n) return ''; if (COUNTRY_NAME_ALIASES[n]) return COUNTRY_NAME_ALIASES[n]; const m = ALL_COUNTRIES.find(c => normalizeJurisdiction(c.name) === n || normalizeJurisdiction(c.isoCode) === n); return m?.name || f; }

function deriveCountryFallback(sel: string, js: DashboardJurisdiction[]): string {
  const ns = normalizeJurisdiction(sel);
  const rc = js.map(j => j.country_name).find(cn => { const nc = normalizeJurisdiction(cn); return !!nc && ns.includes(nc); });
  if (rc) return formatJurisdictionDisplay(rc);
  const parts = formatJurisdictionDisplay(sel).split(/\s*,\s*/).map(s => s.trim()).filter(Boolean);
  const dc = parts.find(p => isKnownCountry(p)) || [...parts].reverse().find(p => isKnownCountry(p));
  if (dc) return resolveCountryName(dc);
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

async function loadArtifactsForJurisdictionCodes(codes: string[]): Promise<RegulationSummary[]> {
  if (!codes.length) return [];
  const res = await Promise.all(codes.slice(0, 5).map(c => cached(`regs:${c}`, 300_000, () => getAllRegulations({ jurisdiction_code: c, limit: 200, sort_by: 'date', sort_order: 'desc' }))));
  const m = new Map<string, RegulationSummary>(); res.forEach(r => r.regulations.forEach(reg => m.set(String(reg.id), reg))); return Array.from(m.values());
}

function formatPolicyType(v?: string): string { const c = v?.trim(); if (!c) return 'General'; return c.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()); }

// ── Donut chart ─────────────────────────────────────────────────────────────

function DonutChart({ compliant, partial, nonCompliant }: { compliant: number; partial: number; nonCompliant: number }) {
  const total = compliant + partial + nonCompliant;
  const r = 58; const strokeWidth = 18; const circumference = 2 * Math.PI * r; const cx = 80; const cy = 80;
  const cFrac = total > 0 ? compliant / total : 0;
  const pFrac = total > 0 ? partial / total : 0;
  const nFrac = total > 0 ? nonCompliant / total : 0;
  const cDash = cFrac * circumference; const pDash = pFrac * circumference; const nDash = nFrac * circumference;
  const gap = total > 0 ? 2 : 0;
  const cOffset = -circumference / 4 + gap / 2; const pOffset = cOffset - cDash + gap; const nOffset = pOffset - pDash + gap;
  return (
    <div className={styles.donutWrapper}>
      <svg width="160" height="160" viewBox="0 0 160 160" style={{ overflow: 'visible' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--ds-surface-2)" strokeWidth={strokeWidth} />
        {total > 0 && (<>
          {compliant > 0 && (<circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--ds-success)" strokeWidth={strokeWidth} strokeDasharray={`${Math.max(cDash - gap, 0)} ${circumference}`} strokeDashoffset={cOffset} strokeLinecap="round" style={{ transition: 'stroke-dasharray 0.6s ease' }} />)}
          {partial > 0 && (<circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--ds-warning)" strokeWidth={strokeWidth} strokeDasharray={`${Math.max(pDash - gap, 0)} ${circumference}`} strokeDashoffset={pOffset} strokeLinecap="round" style={{ transition: 'stroke-dasharray 0.6s ease' }} />)}
          {nonCompliant > 0 && (<circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--ds-danger)" strokeWidth={strokeWidth} strokeDasharray={`${Math.max(nDash - gap, 0)} ${circumference}`} strokeDashoffset={nOffset} strokeLinecap="round" style={{ transition: 'stroke-dasharray 0.6s ease' }} />)}
        </>)}
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="22" fontWeight="700" fill="#101828">{total}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize="11" fill="var(--ds-text-faint)">total</text>
      </svg>
      <div className={styles.donutLegend}>
        <div className={styles.donutLegendItem}><span className={styles.donutLegendDot} style={{ background: 'var(--ds-success)' }} /><span className={styles.donutLegendLabel}>Compliant</span><span className={styles.donutLegendCount}>{compliant}</span></div>
        <div className={styles.donutLegendItem}><span className={styles.donutLegendDot} style={{ background: 'var(--ds-warning)' }} /><span className={styles.donutLegendLabel}>Partial</span><span className={styles.donutLegendCount}>{partial}</span></div>
        <div className={styles.donutLegendItem}><span className={styles.donutLegendDot} style={{ background: 'var(--ds-danger)' }} /><span className={styles.donutLegendLabel}>Non-Compliant</span><span className={styles.donutLegendCount}>{nonCompliant}</span></div>
      </div>
    </div>
  );
}

// ── Page Component ──────────────────────────────────────────────────────────

export default function ComplianceStartPage() {
  const { user, token } = useAuth();
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const { state: wsState, start, resume, reset, setQuestions, setFinalResult } = useCompliance();

  const [mode, setMode] = useState<'loading' | 'history' | 'dashboard' | 'start'>('loading');
  const [assessments, setAssessments] = useState<ComplianceAssessmentSummary[]>([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [storedResult, setStoredResult] = useState<ComplianceFinalResult | null>(null);
  const [storedMeta, setStoredMeta] = useState<{ assessment_id?: string | null; version: number | null; jurisdiction: string | null; assessed_at: string | null } | null>(null);
  const [storedQuestions, setStoredQuestions] = useState<ComplianceLatestResultQuestion[]>([]);
  const [dimensionDetails, setDimensionDetails] = useState<Record<string, ComplianceDimensionDetail>>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [policyIds, setPolicyIds] = useState<string[]>([]);
  const [inputError, setInputError] = useState<string | null>(null);
  const [matchedArtifacts, setMatchedArtifacts] = useState<RegulationSummary[]>([]);
  const [jurisdictionOptions, setJurisdictionOptions] = useState<CompanyJurisdictionOption[]>([]);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState('');
  const [setupLoading, setSetupLoading] = useState(true);
  const [artifactLoading, setArtifactLoading] = useState(false);
  const [artifactSourceNote, setArtifactSourceNote] = useState<string | null>(null);
  const [showAllPolicies, setShowAllPolicies] = useState(false);
  const [inputStep, setInputStep] = useState<'jurisdiction' | 'start'>('jurisdiction');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, { file: File; uploading: boolean; uploaded: boolean; filename: string }[]>>({});
  const [submittingAnswers, setSubmittingAnswers] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'breakdown' | 'gaps' | 'questionnaire' | 'report'>('overview');
  const [breakdownFilter, setBreakdownFilter] = useState({ area: '', status: '' });
  const [expandedGaps, setExpandedGaps] = useState<Set<string>>(new Set());

  const companyId = user?.id?.toString() || '';
  const isIdle = wsState.stage === 'idle';
  const isError = wsState.stage === 'error';
  const isDone = wsState.stage === 'done';
  const isLoading = !isIdle && !isError && !isDone && wsState.stage !== 'awaiting_hitl';
  const isHitl = wsState.stage === 'awaiting_hitl';
  const liveResult = wsState.finalResult;

  // ── On mount: fetch assessment history ─────────────────────────────
  useEffect(() => {
    if (urlSessionId) return;
    (async () => {
      try { const list = await listComplianceAssessments(); setAssessments(list); setMode(list.length > 0 ? 'history' : 'start'); }
      catch { setMode('start'); }
    })();
  }, [urlSessionId]);

  const loadAssessmentVersion = useCallback(async (assessmentId: string) => {
    setAssessmentsLoading(true);
    try {
      const data = await getComplianceAssessmentDetail(assessmentId);
      if (data.final_result) {
        setStoredResult(data.final_result as ComplianceFinalResult);
        setStoredMeta({ assessment_id: data.assessment_id, version: data.version, jurisdiction: data.jurisdiction, assessed_at: data.assessed_at });
        setStoredQuestions(data.questions || []);
        setDimensionDetails(data.dimension_details || {});
        setMode('dashboard');
      }
    } catch { /* ignore */ }
    finally { setAssessmentsLoading(false); }
  }, []);

  useEffect(() => {
    if (isDone && wsState.sessionId && !liveResult) {
      const timer = setTimeout(async () => {
        try {
          const result = await getComplianceResults(wsState.sessionId);
          if ('overall_score' in result) setFinalResult(result as unknown as ComplianceFinalResult);
        } catch { /* ignore */ }
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isDone, wsState.sessionId, liveResult, setFinalResult]);

  useEffect(() => {
    if (liveResult) {
      setStoredResult(liveResult); setStoredMeta(null); setMode('dashboard');
      getComplianceLatestResult().then(data => {
        setDimensionDetails(data.dimension_details || {});
        setStoredQuestions(data.questions || []);
        setStoredMeta({ assessment_id: data.assessment_id, version: data.version, jurisdiction: data.jurisdiction, assessed_at: data.assessed_at });
      }).catch(() => {});
      listComplianceAssessments().then(setAssessments).catch(() => {});
    }
  }, [liveResult]);

  useEffect(() => {
    if (!urlSessionId || !token) return;
    (async () => {
      try {
        const statusRes = await getComplianceStatus(urlSessionId);
        if (statusRes.status === 'complete') {
          const result = await getComplianceResults(urlSessionId);
          if ('overall_score' in result) setFinalResult(result as unknown as ComplianceFinalResult);
        } else if (statusRes.status === 'awaiting_hitl') {
          setMode('start');
          const qs = await getComplianceQuestions(urlSessionId);
          setQuestions(qs.map(q => ({
            question_id: q.question_id,
            dimension_id: q.dimension_id ?? q.obligation_id ?? '',
            policy_id: q.policy_id,
            element_id: q.element_id,
            question_text: q.question_text,
            question_type: q.question_type ?? null,
            document_type: q.document_type ?? null,
            document_description: q.document_description ?? null,
            resolves_question_ids: q.resolves_question_ids ?? null,
            resolves_dimension_ids: q.resolves_dimension_ids ?? null,
          })));
        }
      } catch { setMode('start'); }
    })();
  }, [urlSessionId, token, setFinalResult, setQuestions]);

  useEffect(() => {
    const sid = wsState.sessionId; if (!sid || liveResult) return;
    const h = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const s = await getComplianceStatus(sid);
        if (s.status === 'complete') {
          const r = await getComplianceResults(sid);
          if ('overall_score' in r) setFinalResult(r as unknown as ComplianceFinalResult);
        }
      } catch { /* ignore */ }
    };
    document.addEventListener('visibilitychange', h); return () => document.removeEventListener('visibilitychange', h);
  }, [wsState.sessionId, liveResult, setFinalResult]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSetupLoading(true);
      try {
        const [er, cr] = await Promise.allSettled([cached('enterprise-profile', 120_000, fetchEnterpriseProfile), cached('company-profile', 120_000, fetchCompanyProfile)]);
        if (cancelled) return;
        const ep = er.status === 'fulfilled' ? er.value : null; const cp = cr.status === 'fulfilled' ? cr.value : null;
        const opts = buildJurisdictionOptions(ep, cp);
        const fb = opts[0]?.value || formatJurisdictionDisplay(ep?.jurisdiction) || formatJurisdictionDisplay(ep?.other_jurisdictions?.[0]) || formatJurisdictionDisplay(ep?.new_jurisdictions?.[0]) || formatJurisdictionDisplay(cp?.operating_jurisdictions?.[0] ? [cp.operating_jurisdictions[0].state, cp.operating_jurisdictions[0].country].filter(Boolean).join(', ') : '');
        if (!opts.length && fb) opts.push({ value: fb, label: `Primary · ${fb}`, scope: 'primary' });
        setJurisdictionOptions(opts); setSelectedJurisdiction(prev => prev || fb || '');
        if (!opts.length) setInputError('No company jurisdictions found yet.');
      } catch { if (!cancelled) setInputError('Could not load company jurisdictions.'); }
      finally { if (!cancelled) setSetupLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (setupLoading || !selectedJurisdiction) { setMatchedArtifacts([]); setPolicyIds([]); setArtifactSourceNote(null); return; }
    (async () => {
      setArtifactLoading(true); setArtifactSourceNote(null);
      try {
        const jr = await searchJurisdictionCandidates(selectedJurisdiction); if (cancelled) return;
        const mj = jr.filter(j => matchesDashboardJurisdiction(j, selectedJurisdiction));
        let arts = await loadArtifactsForJurisdictionCodes(getJurisdictionCodeCandidates(mj));
        const fc = deriveCountryFallback(selectedJurisdiction, jr);
        const useFb = !arts.length && !!fc && normalizeJurisdiction(fc) !== normalizeJurisdiction(selectedJurisdiction);
        if (useFb) {
          const cr = await cached(`juris-country:${fc}`, 300_000, () => getDashboardJurisdictions({ q: fc, limit: 100 })); if (cancelled) return;
          const cm = cr.filter(j => matchesDashboardJurisdiction(j, fc) || normalizeJurisdiction(j.country_name) === normalizeJurisdiction(fc));
          arts = await loadArtifactsForJurisdictionCodes(getJurisdictionCodeCandidates(cm));
          if (arts.length) setArtifactSourceNote(`No exact dataset for ${selectedJurisdiction}. Using ${fc}.`);
        }
        if (cancelled) return;
        setMatchedArtifacts(arts); setPolicyIds(arts.map(a => String(a.id)));
        if (!arts.length) setInputError(`No regulation artifacts found for ${selectedJurisdiction}.`); else setInputError(null);
      } catch { if (!cancelled) { setMatchedArtifacts([]); setPolicyIds([]); setInputError('Could not load artifacts.'); } }
      finally { if (!cancelled) setArtifactLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selectedJurisdiction, setupLoading]);

  useEffect(() => { setShowAllPolicies(false); setInputStep('jurisdiction'); }, [selectedJurisdiction]);

  useEffect(() => {
    if (isHitl && wsState.sessionId && wsState.questions.length === 0) {
      (async () => {
        try {
          const qs = await getComplianceQuestions(wsState.sessionId);
          setQuestions(qs.map(q => ({
            question_id: q.question_id,
            dimension_id: q.dimension_id ?? q.obligation_id ?? '',
            policy_id: q.policy_id,
            element_id: q.element_id,
            question_text: q.question_text,
            question_type: q.question_type ?? null,
            document_type: q.document_type ?? null,
            document_description: q.document_description ?? null,
            resolves_question_ids: q.resolves_question_ids ?? null,
            resolves_dimension_ids: q.resolves_dimension_ids ?? null,
          })));
        } catch { /* ignore */ }
      })();
    }
  }, [isHitl, wsState.sessionId, wsState.questions.length, setQuestions]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleRerun = () => { reset(); setMode('start'); setActiveTab('overview'); setAnswers({}); setUploadedFiles({}); };

  const handleStart = () => {
    if (!selectedJurisdiction) { setInputError('Select a jurisdiction.'); return; }
    if (!policyIds.length) { setInputError('No policies available.'); return; }
    setInputError(null);
    start(companyId, policyIds, token!, selectedJurisdiction);
  };

  const handleFileSelect = (qid: string, file: File) => {
    setUploadedFiles(prev => ({ ...prev, [qid]: [...(prev[qid] || []), { file, uploading: false, uploaded: false, filename: file.name }] }));
  };

  const handleSubmitAnswers = async () => {
    setSubmittingAnswers(true);
    try {
      for (const q of wsState.questions) {
        const files = uploadedFiles[q.question_id] || [];
        for (let i = 0; i < files.length; i++) {
          if (!files[i].uploaded) {
            setUploadedFiles(p => { const arr = [...(p[q.question_id] || [])]; arr[i] = { ...arr[i], uploading: true }; return { ...p, [q.question_id]: arr }; });
            try {
              await uploadComplianceDocument(wsState.sessionId, q.question_id, files[i].file);
              setUploadedFiles(p => { const arr = [...(p[q.question_id] || [])]; arr[i] = { ...arr[i], uploading: false, uploaded: true }; return { ...p, [q.question_id]: arr }; });
            } catch {
              setUploadedFiles(p => { const arr = [...(p[q.question_id] || [])]; arr[i] = { ...arr[i], uploading: false }; return { ...p, [q.question_id]: arr }; });
            }
          }
        }
      }
      const all = wsState.questions.map(q => {
        const files = uploadedFiles[q.question_id] || [];
        const txt = answers[q.question_id] || '';
        const fileRefs = files.filter(f => f.uploaded).map(f => `[Document uploaded: ${f.filename}]`).join('\n');
        return { question_id: q.question_id, answer_text: [fileRefs, txt].filter(Boolean).join('\n') || '' };
      });
      if (all.length > 0) await submitComplianceAnswers(wsState.sessionId, all);
      resume(wsState.sessionId, token!);
    } catch { /* ignore */ }
    finally { setSubmittingAnswers(false); }
  };

  const toggleGap = (id: string) => { setExpandedGaps(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleRow = (id: string) => { setExpandedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };

  const selectedOption = jurisdictionOptions.find(o => o.value === selectedJurisdiction);
  const visibleArtifacts = showAllPolicies ? matchedArtifacts : matchedArtifacts.slice(0, 3);
  const togglePolicy = (id: string) => { setPolicyIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]); };
  const fr = storedResult;

  // ── Render ────────────────────────────────────────────────────────────────

  if (mode === 'loading') {
    return <div className={styles.page}><div className={styles.container}><p className={styles.inputNote} style={{ padding: '60px 0', textAlign: 'center' }}>Loading compliance assessment results...</p></div></div>;
  }

  // ═══════════════════════════════════════════════════════════════════
  //  MODE: HISTORY
  // ═══════════════════════════════════════════════════════════════════
  if (mode === 'history') {
    const completedAssessments = assessments.filter(a => a.status === 'complete');
    const runningAssessments = assessments.filter(a => a.status !== 'complete');
    return (
      <div className={styles.page}><div className={styles.container}>
        <div className={styles.historyHeader}>
          <div><span className={styles.headerEyebrow}>Compliance</span><h1 className={styles.title}>Compliance Assessment History</h1><p className={styles.subtitle}>All compliance assessments for your organisation. Click any row to view the full results.</p></div>
          <button className={styles.startBtn} style={{ width: 'auto', padding: '10px 24px' }} onClick={() => { reset(); setMode('start'); }}>New Compliance Analysis</button>
        </div>
        {assessmentsLoading && <p className={styles.inputNote} style={{ textAlign: 'center', padding: '20px 0' }}>Loading assessment...</p>}
        {completedAssessments.length === 0 && !assessmentsLoading && (
          <div className={styles.historyEmpty}><p>No completed compliance assessments yet.</p><button className={styles.startBtn} style={{ width: 'auto', padding: '10px 24px', marginTop: 12 }} onClick={() => { reset(); setMode('start'); }}>Start your first compliance assessment</button></div>
        )}
        {completedAssessments.length > 0 && (
          <table className={styles.historyTable}><thead><tr><th>Version</th><th>Jurisdiction</th><th>Score</th><th>Health</th><th>Obligations</th><th>Coverage</th><th>Assessed</th></tr></thead>
          <tbody>{completedAssessments.map(a => {
            const score = a.overall_score != null ? Math.round(a.overall_score) : '-';
            const coverage = a.coverage_score != null ? `${Math.round(a.coverage_score * 100)}%` : '-';
            const dims = [a.dimensions_met != null ? `${a.dimensions_met} compliant` : null, a.dimensions_partial != null ? `${a.dimensions_partial} partial` : null, a.dimensions_missing != null ? `${a.dimensions_missing} non-compliant` : null].filter(Boolean).join(', ') || (a.total_dimensions ? `${a.total_dimensions} total` : '-');
            const dateStr = a.assessed_at ? new Date(a.assessed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
            return (<tr key={a.assessment_id || a.session_id} className={styles.historyRow} onClick={() => a.assessment_id && loadAssessmentVersion(a.assessment_id)}>
              <td><span className={styles.historyVersion}>v{a.version ?? '?'}</span></td><td>{a.jurisdiction || '-'}</td>
              <td><span className={styles.historyScore} data-rating={a.health_rating}>{score}</span></td>
              <td><span className={styles.completeBadge} data-rating={a.health_rating} style={{ padding: '4px 12px', fontSize: 11 }}>{a.health_rating || '-'}</span></td>
              <td className={styles.historyDims}>{dims}</td><td>{coverage}</td><td className={styles.historyDate}>{dateStr}</td>
            </tr>);
          })}</tbody></table>
        )}
        {runningAssessments.length > 0 && (
          <div className={styles.historyRunning}><h3>In progress</h3>{runningAssessments.map(a => (<div key={a.session_id} className={styles.historyRunningRow}><span className={styles.historyVersion}>v{a.version ?? '?'}</span><span>{a.jurisdiction || 'Unknown jurisdiction'}</span><span className={styles.historyRunningStatus}>{a.current_node ? `At: ${a.current_node.replace(/_/g, ' ')}` : a.status}</span><span className={styles.historyDate}>{a.created_at ? new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span></div>))}</div>
        )}
      </div></div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  //  MODE: DASHBOARD
  // ═══════════════════════════════════════════════════════════════════
  if (mode === 'dashboard' && fr) {
    const score = fr.overall_score;                    // 0-100 int
    const total = fr.total_obligations || 1;
    const coveragePct = Math.round(((fr.obligations_compliant + fr.obligations_partial) / total) * 100);
    const domains = fr.per_domain_scores || {};

    // Heatmap from obligation_statuses
    const heatmap: Record<string, Record<string, number>> = {};
    (fr.obligation_statuses || []).forEach(s => {
      if (s.status === 'compliant') return;
      const domain = s.domain || 'unknown';
      const sev = s.severity || 'low';
      if (!heatmap[domain]) heatmap[domain] = { critical: 0, high: 0, medium: 0, low: 0 };
      heatmap[domain][sev] = (heatmap[domain][sev] || 0) + 1;
    });

    // All gaps sorted by severity
    const gaps = (fr.obligation_statuses || []).filter(s => s.status !== 'compliant');
    const criticalGaps = gaps.filter(s => s.severity === 'critical').slice(0, 5);

    return (
      <div className={styles.page}><div className={styles.container}>
        <div className={`${styles.dashboard} ${styles.dashboardVisible}`}>
          {assessments.length > 0 && <button className={styles.historyBackBtn} onClick={() => setMode('history')}>&larr; All assessments</button>}
          <div className={styles.dashboardHeader}>
            <div><h1 className={styles.title}>Compliance Assessment</h1><p className={styles.dashboardMeta}>{storedMeta?.version ? `Version ${storedMeta.version} · ` : ''}{fr.assessed_at ? `Completed ${new Date(fr.assessed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}{storedMeta?.jurisdiction ? ` · ${storedMeta.jurisdiction}` : ''}</p></div>
            <div className={styles.dashboardActions}>
              <span className={styles.completeBadge} data-rating={fr.no_applicable_obligations ? 'amber' : fr.health_rating}>
                {fr.no_applicable_obligations ? 'Nothing assessed' : 'Assessment complete'}
              </span>
              <button className={styles.startBtn} onClick={handleRerun} style={{ width: 'auto', padding: '8px 18px', fontSize: 13 }}>New Analysis</button>
            </div>
          </div>

          {/* A run that produced zero obligations scores a placeholder 100/green
              ("nothing to fail against"). Showing that as an all-clear tells the
              user they are fully compliant when NOTHING was actually checked —
              so say plainly that the score is not evidence of compliance. */}
          {fr.no_applicable_obligations && (
            <div style={{
              margin: '0 0 20px', padding: '14px 18px', borderRadius: 10,
              background: 'rgba(247,144,9,0.10)', border: '1px solid rgba(247,144,9,0.45)',
              color: 'var(--ds-text)', fontSize: 14, lineHeight: 1.55,
            }}>
              <strong>⚠ No obligations were assessed — this score is not a compliance result.</strong>
              <div style={{ marginTop: 6 }}>
                {fr.status_note || 'No applicable obligations were found in the selected policies.'}
              </div>
              <div style={{ marginTop: 6, opacity: 0.85 }}>
                The 100 / green figures below are placeholders meaning “nothing to fail against”, not
                evidence of compliance. Check that your company profile lists the jurisdictions you
                operate in, then run a new analysis.
              </div>
            </div>
          )}

          <div className={styles.tabBar}>
            {(['overview', 'breakdown', 'gaps', 'questionnaire', 'report'] as const).map(tab => (
              <button key={tab} className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`} onClick={() => setActiveTab(tab)}>
                {tab === 'overview' ? 'Overview' : tab === 'breakdown' ? 'Obligation breakdown' : tab === 'gaps' ? 'Gaps & recommendations' : tab === 'questionnaire' ? 'Questionnaire' : 'Formal report'}
              </button>
            ))}
          </div>

          {/* ── Overview ────────────────────────────────────── */}
          {activeTab === 'overview' && (
            <div className={styles.tabContent}>
              <div className={styles.metricsRow}>
                <div className={styles.metricCard}>
                  <div className={styles.metricInfoBtn}>i<span className={styles.metricTooltip}>Overall compliance score (0 = non-compliant, 100 = fully compliant)</span></div>
                  <div className={styles.metricValue} data-rating={fr.health_rating}>{score}</div>
                  <div className={styles.metricLabel}>Compliance Score</div>
                </div>
                <div className={styles.metricCard}>
                  <div className={styles.metricInfoBtn}>i<span className={styles.metricTooltip}>Obligations with at least partial evidence</span></div>
                  <div className={styles.metricValue}>{coveragePct}%</div>
                  <div className={styles.metricLabel}>Coverage</div>
                </div>
                <div className={styles.metricCard}>
                  <div className={styles.metricInfoBtn}>i<span className={styles.metricTooltip}>Total atomic obligations evaluated</span></div>
                  <div className={styles.metricValue}>{fr.total_obligations}</div>
                  <div className={styles.metricLabel}>Obligations</div>
                </div>
                <div className={styles.metricCard}>
                  <div className={styles.metricInfoBtn}>i<span className={styles.metricTooltip}>Qualitative compliance rating</span></div>
                  <span className={styles.maturityBadge}>{(fr.health_rating || 'red').toUpperCase()}</span>
                  <div className={styles.metricLabel}>Health</div>
                </div>
              </div>

              <div className={styles.twoCol}>
                <div className={styles.areaChart}>
                  <h3>Domain compliance</h3>
                  {Object.entries(domains).map(([domain, data]) => {
                    const s = data.score || 0;
                    const c = s >= 75 ? 'var(--ds-success)' : s >= 40 ? 'var(--ds-warning)' : data.total === 0 ? 'var(--ds-border-strong)' : 'var(--ds-danger)';
                    const showLabelInside = s > 15;
                    return (
                      <div key={domain} className={styles.areaBar}>
                        <span className={styles.areaLabel}>{AREA_LABELS[domain] || domain}</span>
                        <div className={styles.areaTrack} style={{ height: 12 }}>
                          <div className={styles.areaFill} style={{ width: `${Math.max(s, 2)}%`, background: c, height: '100%', position: 'relative' }}>
                            {showLabelInside && <span className={styles.areaFillLabel}>{s}%</span>}
                          </div>
                        </div>
                        {!showLabelInside && <span className={styles.areaValue} style={{ color: c }}>{data.total > 0 ? `${s}%` : 'N/A'}</span>}
                        {showLabelInside && <span className={styles.areaValue} style={{ color: 'var(--ds-text-faint)', fontSize: 11 }}>&nbsp;</span>}
                      </div>
                    );
                  })}
                </div>
                <div className={styles.summaryCol}>
                  <div className={styles.statusSummaryCard}>
                    <h3>Status summary</h3>
                    <DonutChart compliant={fr.obligations_compliant} partial={fr.obligations_partial} nonCompliant={fr.obligations_non_compliant} />
                  </div>
                </div>
              </div>

              {/* Gap heatmap */}
              {Object.keys(heatmap).length > 0 && (
                <div className={styles.statusSummaryCard}>
                  <h3>Gap heatmap</h3>
                  <table className={styles.breakdownTable}>
                    <thead><tr><th>Domain</th><th style={{color:'var(--ds-danger-text)'}}>Critical</th><th style={{color:'var(--ds-warning-text)'}}>High</th><th style={{color:'var(--ds-warning-text)'}}>Medium</th><th style={{color:'var(--ds-success-text)'}}>Low</th></tr></thead>
                    <tbody>{Object.entries(heatmap).map(([domain, counts]) => (
                      <tr key={domain}><td className={styles.breakdownReq}>{AREA_LABELS[domain] || domain}</td><td>{counts.critical || 0}</td><td>{counts.high || 0}</td><td>{counts.medium || 0}</td><td>{counts.low || 0}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              )}

              {/* Merge funnel */}
              {fr.merge_stats && (
                <div className={styles.twoCol}>
                  <div className={styles.statusSummaryCard}>
                    <h3>Obligation reduction funnel</h3>
                    <div className={styles.funnelSteps}>
                      <div className={styles.funnelStep}><span className={styles.funnelValue}>{fr.merge_stats.raw_count}</span><span className={styles.funnelLabel}>Raw</span></div>
                      <span className={styles.funnelArrow}>&rarr;</span>
                      <div className={styles.funnelStep}><span className={styles.funnelValue}>{fr.merge_stats.post_dedup_count}</span><span className={styles.funnelLabel}>Deduped</span></div>
                      {fr.merge_stats.post_filter_count != null && (<>
                        <span className={styles.funnelArrow}>&rarr;</span>
                        <div className={styles.funnelStep}><span className={styles.funnelValue}>{fr.merge_stats.post_filter_count}</span><span className={styles.funnelLabel}>Filtered</span></div>
                      </>)}
                    </div>
                  </div>
                  {fr.merge_stats.deferred_count != null && fr.merge_stats.deferred_count > 0 && (
                    <div className={styles.statusSummaryCard}>
                      <h3>Deferred obligations</h3>
                      <p className={styles.inputNote}>{fr.merge_stats.deferred_count} low-priority not assessed.</p>
                      <div className={styles.deferredList}>
                        {fr.merge_stats.deferred_by_domain && Object.entries(fr.merge_stats.deferred_by_domain).map(([a, c]) => (<div key={a} className={styles.summaryRow}><span>{AREA_LABELS[a] || a}</span><strong>{c}</strong></div>))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {criticalGaps.length > 0 && (
                <div className={styles.criticalGapsCard}>
                  <h3>Critical gaps</h3>
                  {criticalGaps.map((g, i) => (
                    <div key={g.obligation_id || i} className={styles.gapRow}>
                      <button className={styles.gapRowHeader} onClick={() => toggleGap(g.obligation_id)}>
                        <span className={styles.gapDot} data-severity={g.severity || 'critical'} />
                        <span className={styles.gapTitle}>{g.description || g.obligation_id}</span>
                        <span className={styles.severityBadge} data-sev={g.severity || 'critical'}>{g.severity || 'critical'}</span>
                        <span className={styles.areaBadge}>{AREA_LABELS[g.domain || ''] || g.domain}</span>
                      </button>
                      {expandedGaps.has(g.obligation_id) && (
                        <div className={styles.gapBody}>
                          {g.gap && <p className={styles.gapDesc}>{g.gap}</p>}
                          {g.evidence_refs && g.evidence_refs.length > 0 && (
                            <div className={styles.gapEvidence}><strong>Evidence used:</strong><ul>{g.evidence_refs.map((e, j) => <li key={j}>{e}</li>)}</ul></div>
                          )}
                          {g.remediation && <p className={styles.gapRec}>{g.remediation}</p>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Breakdown ────────────────────────────────────── */}
          {activeTab === 'breakdown' && (
            <div className={styles.tabContent}>
              <div className={styles.breakdownFilters}>
                <select value={breakdownFilter.area} onChange={e => setBreakdownFilter(p => ({ ...p, area: e.target.value }))}>
                  <option value="">All domains</option>
                  {Object.entries(AREA_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select value={breakdownFilter.status} onChange={e => setBreakdownFilter(p => ({ ...p, status: e.target.value }))}>
                  <option value="">All statuses</option>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {(() => {
                const filtered = (fr.obligation_statuses || [])
                  .filter(r => {
                    const area = dimensionDetails[r.obligation_id]?.area || r.domain || '';
                    if (breakdownFilter.area && area !== breakdownFilter.area) return false;
                    if (breakdownFilter.status && r.status !== breakdownFilter.status) return false;
                    return true;
                  })
                  .slice().sort((a, b) => (SEVERITY_ORDER[a.severity || 'low'] ?? 3) - (SEVERITY_ORDER[b.severity || 'low'] ?? 3));

                return (
                  <>
                    <div className={styles.breakdownCount}>{filtered.length} of {fr.obligation_statuses?.length ?? 0} obligations</div>
                    <table className={styles.breakdownTable}>
                      <thead>
                        <tr>
                          <th>Obligation</th>
                          <th>Domain</th>
                          <th>Status</th>
                          <th>Severity</th>
                          <th>Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map(r => {
                          const detail = dimensionDetails[r.obligation_id];
                          const isExpanded = expandedRows.has(r.obligation_id);
                          const area = detail?.area ?? r.domain ?? '';
                          const sourcePolicyName = detail?.source_policy_name ?? undefined;
                          const policyReference = detail?.policy_reference ?? r.article_id ?? undefined;
                          const reasoning = detail?.reasoning ?? r.reason ?? undefined;
                          const companyEvidence = detail?.company_evidence_summary ?? undefined;
                          const gapText = detail?.gap ?? r.gap;
                          const recommendationText = detail?.recommendation ?? r.remediation;
                          const evidenceUsed = detail?.evidence_used ?? r.evidence_refs;
                          const docSources = detail?.doc_sources ?? undefined;
                          const questionText = detail?.question || r.description || r.obligation_id;
                          const scoreContrib = r.score_contribution ?? detail?.score_contribution;
                          return (
                            <React.Fragment key={r.obligation_id}>
                              <tr
                                className={`${styles.breakdownRow} ${isExpanded ? styles.breakdownRowSelected : ''}`}
                                onClick={() => toggleRow(r.obligation_id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <td className={styles.breakdownReq}>{questionText}</td>
                                <td><span className={styles.areaBadge}>{AREA_LABELS[area] || area}</span></td>
                                <td><span className={styles.statusBadge} data-status={r.status}>{STATUS_LABELS[r.status] || r.status}</span></td>
                                <td>{r.severity ? <span className={styles.severityBadge} data-sev={r.severity}>{r.severity}</span> : '-'}</td>
                                <td>{scoreContrib != null ? `${Math.round(scoreContrib * 100)}%` : '-'}</td>
                              </tr>
                              <tr className={styles.breakdownDetailTr}>
                                <td colSpan={5}>
                                  <div className={`${styles.breakdownDetailPanel} ${isExpanded ? styles.open : ''}`}>
                                    <div className={styles.breakdownDetailCard}>
                                      <button
                                        className={styles.breakdownDetailClose}
                                        onClick={(e) => { e.stopPropagation(); toggleRow(r.obligation_id); }}
                                      >✕ Close</button>
                                      <div className={styles.breakdownDetailTitle}>{questionText}</div>
                                      <div className={styles.breakdownDetailBadges}>
                                        <span className={styles.statusBadge} data-status={r.status}>{STATUS_LABELS[r.status] || r.status}</span>
                                        {r.severity && <span className={styles.severityBadge} data-sev={r.severity}>{r.severity}</span>}
                                        <span className={styles.areaBadge}>{AREA_LABELS[area] || area}</span>
                                      </div>
                                      {sourcePolicyName && (
                                        <div className={styles.breakdownDetailSection}><strong>Regulation:</strong> {sourcePolicyName}</div>
                                      )}
                                      {policyReference && (
                                        <div className={styles.breakdownDetailSection}><strong>Clause / Article:</strong> {policyReference}</div>
                                      )}
                                      {reasoning && (
                                        <div className={styles.breakdownDetailSection}><strong>Reasoning:</strong> {reasoning}</div>
                                      )}
                                      {companyEvidence && (
                                        <div className={styles.breakdownDetailSection}><strong>Company Evidence:</strong> {companyEvidence}</div>
                                      )}
                                      {gapText && (
                                        <div className={`${styles.breakdownDetailSection} ${styles.gap}`}><strong>Gap:</strong> {gapText}</div>
                                      )}
                                      {recommendationText && (
                                        <div className={`${styles.breakdownDetailSection} ${styles.rec}`}><strong>Remediation:</strong> {recommendationText}</div>
                                      )}
                                      {evidenceUsed && evidenceUsed.length > 0 && (
                                        <div className={styles.breakdownDetailSection}>
                                          <strong>Evidence Used:</strong>
                                          <ul className={styles.breakdownDetailEvidenceList}>
                                            {evidenceUsed.map((e, i) => <li key={i}>{e}</li>)}
                                          </ul>
                                        </div>
                                      )}
                                      {docSources && docSources.length > 0 && (
                                        <div className={styles.breakdownDetailSection}><strong>Source Docs:</strong> {docSources.join(', ')}</div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                );
              })()}
            </div>
          )}

          {/* ── Gaps ─────────────────────────────────────────── */}
          {activeTab === 'gaps' && (
            <div className={styles.tabContent}>
              <div className={styles.gapsTwoCol}>
                <div className={styles.gapsLeftCol}>
                  <h3 className={styles.gapsLeftColHeader}>All non-compliant obligations</h3>
                  {gaps.sort((a, b) => (SEVERITY_ORDER[a.severity || 'low'] ?? 3) - (SEVERITY_ORDER[b.severity || 'low'] ?? 3)).map((g, i) => (
                    <div key={g.obligation_id || i} className={styles.gapCard}>
                      <div className={styles.gapCardHeader}><span className={styles.gapDot} data-severity={g.severity || 'medium'} /><span className={styles.gapCardTitle}>{g.description || g.obligation_id}</span></div>
                      {g.gap && <p className={styles.gapCardDesc}>{g.gap}</p>}
                      <div className={styles.gapCardBadges}>
                        {g.severity && <span className={styles.severityBadge} data-sev={g.severity}>{g.severity}</span>}
                        {g.domain && <span className={styles.areaBadge}>{AREA_LABELS[g.domain] || g.domain}</span>}
                        <span style={{ fontSize: 11, color: 'var(--ds-text-muted)' }}>{STATUS_LABELS[g.status] || g.status}</span>
                      </div>
                    </div>
                  ))}
                  {gaps.length === 0 && <p className={styles.noGaps}>All obligations compliant.</p>}
                </div>
                <div className={styles.recsColSticky}>
                  <div className={styles.recsCard}>
                    <h3>Prioritised remediation plan</h3>
                    <ol className={styles.recsList}>{(fr.remediation_plan?.items || []).map((r, i) => (<li key={r.obligationId || i} className={styles.recItem}><p><strong>{r.policyName} — {r.articleReference}</strong></p><p>{r.recommendedAction}</p><span className={styles.severityBadge} data-sev={r.estimatedEffort === 'high' ? 'critical' : r.estimatedEffort === 'medium' ? 'high' : 'medium'}>{r.estimatedEffort} effort</span> <span style={{ fontSize: 11, color: 'var(--ds-text-muted)' }}>{r.deadlineSuggestion}</span>{r.policyLastUpdated && <span style={{ fontSize: 11, color: 'var(--ds-text-muted)' }} title="When the source policy's content was last fetched into our database"> · policy data as of {String(r.policyLastUpdated).split('T')[0]}</span>}</li>))}</ol>
                    {(fr.remediation_plan?.items || []).length === 0 && <p className={styles.noGaps}>No remediation needed.</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Questionnaire ────────────────────────────────── */}
          {/* SRS #67: formal, versioned, signed-off assessment report */}
          {activeTab === 'report' && (
            <div className={styles.tabContent}>
              <ComplianceReportPanel sessionId={fr.session_id} />
            </div>
          )}

          {activeTab === 'questionnaire' && (
            <div className={styles.tabContent}>
              {storedQuestions.length > 0 ? storedQuestions.map(q => {
                const obStatus = (fr.obligation_statuses || []).find(r => r.obligation_id === q.dimension_id);
                const latest = q.latest_answer;
                return (
                  <div key={q.question_id} className={styles.qaCard}>
                    <div className={styles.qaCardHeader}>
                      <span className={styles.qaTypeBadge}>{q.question_type === 'document_request' ? 'Document' : 'Question'}</span>
                      <span className={styles.qaTypeBadge} style={{ marginLeft: 4 }}>#{q.question_id.slice(0, 8)}</span>
                    </div>
                    <div className={styles.qaQuestion}><strong>Q:</strong> {q.question_text}</div>
                    <div className={styles.qaAnswer}><strong>A:</strong>{' '}
                      {latest ? (<>
                        {latest.answer_text || ''}
                        {latest.files.length > 0 && (<div style={{ marginTop: 4 }}>{latest.files.map(f => (<div key={f.id} className={styles.fileSelectedName} style={{ marginBottom: 2 }}>{f.file_name || f.file_s3_key}</div>))}</div>)}
                        {!latest.answer_text && latest.files.length === 0 && <em>No answer provided</em>}
                      </>) : (<em>No answer provided</em>)}
                    </div>
                    {obStatus && (<div className={styles.qaOutcomeBadge}><span style={{ color: 'var(--ds-text-faint)', fontWeight: 400 }}>Resolved as</span><span className={styles.statusBadge} data-status={obStatus.status}>{STATUS_LABELS[obStatus.status] || obStatus.status}</span></div>)}
                  </div>
                );
              }) : <p className={styles.noGaps}>No questionnaire was needed for this assessment.</p>}
            </div>
          )}
        </div>
      </div></div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  //  MODE: START — jurisdiction picker + running + HITL
  // ═══════════════════════════════════════════════════════════════════
  return (
    <div className={styles.page}><div className={styles.container}>
      {isIdle && !liveResult && (<>
        <div className={styles.inputHeader}>
          <span className={styles.headerEyebrow}>Compliance workflow</span>
          <p className={styles.subtitle}>Select a jurisdiction, then start the compliance assessment.</p>
          <div className={styles.flowSteps}>
            <button type="button" className={`${styles.flowStep} ${inputStep === 'jurisdiction' ? styles.flowStepActive : ''}`} onClick={() => setInputStep('jurisdiction')}><span className={styles.flowStepNumber}>1</span><div className={styles.flowStepCopy}><strong>Select jurisdiction</strong><span>Choose the region to evaluate.</span></div></button>
            <button type="button" className={`${styles.flowStep} ${inputStep === 'start' ? styles.flowStepActive : ''} ${!selectedJurisdiction ? styles.flowStepDisabled : ''}`} onClick={() => { if (selectedJurisdiction) setInputStep('start'); }} disabled={!selectedJurisdiction}><span className={styles.flowStepNumber}>2</span><div className={styles.flowStepCopy}><strong>Start compliance assessment</strong><span>Review and run.</span></div></button>
          </div>
        </div>
        <div className={styles.inputCard}>
          {inputStep === 'jurisdiction' ? (
            <div className={styles.inputMain}>
              <div className={styles.selectorBlock}>
                <span className={styles.sectionEyebrow}>Step 1</span><h2 className={styles.sectionTitle}>Select Jurisdiction</h2><p className={styles.sectionText}>This determines which policies are recommended.</p>
                <label htmlFor="compliance-jurisdiction" className={styles.fieldLabel}>Company jurisdiction</label>
                <select id="compliance-jurisdiction" className={styles.jurisdictionSelect} value={selectedJurisdiction} onChange={e => setSelectedJurisdiction(e.target.value)} disabled={setupLoading}><option value="">{setupLoading ? 'Loading...' : 'Select a jurisdiction'}</option>{jurisdictionOptions.map(o => <option key={`${o.scope}-${o.value}`} value={o.value}>{o.label}</option>)}</select>
                {selectedJurisdiction && selectedOption && (<div className={styles.selectedJurisdictionCard}><div><span className={styles.selectedJurisdictionLabel}>Selected</span><div className={styles.selectedJurisdictionValue}>{selectedJurisdiction}</div></div><span className={`${styles.scopeBadge} ${styles[`scope_${selectedOption.scope}`]}`}>{SCOPE_LABELS[selectedOption.scope]}</span></div>)}
                <p className={styles.inputNote}>Choose a jurisdiction and matching policies will load.</p>
              </div>
              <div className={styles.autoArtifactsBox}>
                <div className={styles.autoArtifactsHeader}><div><span className={styles.autoArtifactsEyebrow}>Preview</span><span className={styles.autoArtifactsTitle}>Recommended policies</span></div><span className={styles.autoArtifactsCount}>{policyIds.length} selected</span></div>
                {artifactSourceNote && <p className={styles.fallbackNotice}>{artifactSourceNote}</p>}
                {artifactLoading ? <p className={styles.inputNote}>Loading policies...</p> : matchedArtifacts.length > 0 ? (<>
                  <div className={styles.policyList}>{visibleArtifacts.map(a => { const selected = policyIds.includes(String(a.id)); return (<div key={a.id} className={`${styles.policyItem} ${!selected ? styles.policyItemDeselected : ''}`} onClick={() => togglePolicy(String(a.id))} role="checkbox" aria-checked={selected} tabIndex={0} onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); togglePolicy(String(a.id)); } }}><span className={`${styles.policyCheckbox} ${selected ? styles.policyCheckboxChecked : ''}`}>{selected && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}</span><div><div className={styles.policyItemTitle}>{a.title}</div><div className={styles.policyItemMeta}><span className={styles.policyTypeBadge}>{formatPolicyType(a.regulation_type || a.content_type)}</span><span>{a.jurisdiction_name || selectedJurisdiction}</span><span className={styles.policyId}>#{a.id}</span></div></div></div>); })}</div>
                  {matchedArtifacts.length > 3 && <button type="button" className={styles.viewAllBtn} onClick={() => setShowAllPolicies(p => !p)}>{showAllPolicies ? 'Show fewer' : `View all ${matchedArtifacts.length}`}</button>}
                  <div className={styles.continueRow}><button type="button" className={styles.startBtn} onClick={() => setInputStep('start')}>Continue to Start Assessment</button></div>
                </>) : <p className={styles.inputNote}>{selectedJurisdiction ? 'No policies found.' : 'Select a jurisdiction first.'}</p>}
              </div>
            </div>
          ) : (
            <div className={styles.summaryPanel}><div className={styles.summaryCard}>
              <span className={styles.summaryEyebrow}>Step 2</span><h3 className={styles.summaryTitle}>Start Compliance Assessment</h3>
              <div className={styles.summaryList}><div className={styles.summaryRow}><span>Jurisdiction</span><strong>{selectedJurisdiction}</strong></div><div className={styles.summaryRow}><span>Policies</span><strong>{policyIds.length}</strong></div></div>
              {(inputError || wsState.error) && <p className={styles.errorMsg}>{inputError || wsState.error}</p>}
              <div className={styles.startRow}>
                <button type="button" className={styles.btnSecondary} onClick={() => setInputStep('jurisdiction')}>Back</button>
                {storedResult && <button type="button" className={styles.btnSecondary} onClick={() => setMode('dashboard')}>Cancel</button>}
                <button className={styles.startBtn} onClick={handleStart} disabled={!selectedJurisdiction || !policyIds.length || setupLoading || artifactLoading}>Start Compliance Assessment ({policyIds.length} policies)</button>
              </div>
            </div></div>
          )}
        </div>
      </>)}

      {(isLoading || isHitl || isError) && !liveResult && (
        <div className={styles.loadingWrapper}>
          <ComplianceVisualizer
            stage={wsState.stage}
            stageMessage={wsState.stageMessage}
            stepperNodes={wsState.stepperNodes}
            areasProcessed={wsState.areasProcessed}
            dimensionsExtracted={wsState.dimensionsExtracted}
            comparisonProgress={wsState.comparisonProgress}
            questionsCount={wsState.questionsCount}
            eventLog={wsState.eventLog}
            nodeStats={wsState.nodeStats}
          />
          {isHitl && wsState.questions.length > 0 && (
            <div className={styles.hitlSection}>
              <div className={styles.hitlBanner}>{wsState.questions.length} item{wsState.questions.length !== 1 ? 's' : ''} need your input</div>
              {wsState.questions.filter(q => q.question_type === 'document_request').length > 0 && (
                <div className={styles.hitlGroup}>
                  <h3 className={styles.hitlGroupTitle}>Document uploads</h3>
                  <p className={styles.hitlGroupDesc}>Upload multiple files or type — both accepted.</p>
                  {wsState.questions.filter(q => q.question_type === 'document_request').map(q => { const files = uploadedFiles[q.question_id] || []; return (
                    <div key={q.question_id} className={styles.docUploadCard}>
                      <div className={styles.docUploadHeader}>
                        <span className={styles.docTypeBadge}>{q.document_type || 'Document'}</span>
                        {q.resolves_question_ids && <span className={styles.docResolveCount}>Resolves {q.resolves_question_ids.length}</span>}
                      </div>
                      <p className={styles.docUploadPrompt}>{q.question_text}</p>
                      {q.document_description && <p className={styles.docUploadDesc}>{q.document_description}</p>}
                      <div className={styles.docUploadActions}>
                        <label className={styles.fileInputLabel}>
                          <input type="file" accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx" className={styles.fileInputHidden} onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(q.question_id, f); }} />Add file
                        </label>
                        <span className={styles.inputNote}>{files.length} file{files.length !== 1 ? 's' : ''} attached</span>
                      </div>
                      {files.map((fe, i) => (<div key={i} className={styles.fileSelectedName} style={{ marginBottom: 2 }}>{fe.uploaded ? '\u2713 ' : fe.uploading ? '\u25CB ' : ''}{fe.filename}</div>))}
                      <textarea className={styles.docAltText} value={answers[q.question_id] || ''} onChange={e => setAnswers(p => ({ ...p, [q.question_id]: e.target.value }))} placeholder="Or type an answer..." />
                    </div>
                  ); })}
                </div>
              )}
              {wsState.questions.filter(q => q.question_type !== 'document_request').length > 0 && (
                <div className={styles.hitlGroup}>
                  <h3 className={styles.hitlGroupTitle}>Questions</h3>
                  <p className={styles.hitlGroupDesc}>Text or file — both accepted.</p>
                  {wsState.questions.filter(q => q.question_type !== 'document_request').map(q => { const files = uploadedFiles[q.question_id] || []; return (
                    <div key={q.question_id} className={styles.docUploadCard}>
                      <p className={styles.docUploadPrompt}>{q.question_text}</p>
                      <textarea className={styles.docAltText} value={answers[q.question_id] || ''} onChange={e => setAnswers(p => ({ ...p, [q.question_id]: e.target.value }))} placeholder="Enter answer..." style={{ minHeight: 60 }} />
                      <div className={styles.docUploadActions} style={{ marginTop: 8 }}>
                        <label className={styles.fileInputLabel} style={{ background: 'var(--ds-text-muted)' }}>
                          <input type="file" accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx" className={styles.fileInputHidden} onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(q.question_id, f); }} />Attach file
                        </label>
                        {files.length > 0 && <span className={styles.inputNote}>{files.length} file{files.length !== 1 ? 's' : ''}</span>}
                      </div>
                      {files.map((fe, i) => (<div key={i} className={styles.fileSelectedName} style={{ marginBottom: 2 }}>{fe.uploaded ? '\u2713 ' : fe.uploading ? '\u25CB ' : ''}{fe.filename}</div>))}
                    </div>
                  ); })}
                </div>
              )}
              <div className={styles.submitRow}><button className={styles.startBtn} onClick={handleSubmitAnswers} disabled={submittingAnswers}>{submittingAnswers ? 'Submitting...' : 'Submit all responses'}</button></div>
            </div>
          )}
          {isError && (<div className={styles.errorBanner}><p>{wsState.error}</p><button className={styles.startBtn} onClick={reset}>Retry</button></div>)}
        </div>
      )}
    </div></div>
  );
}
